/** CMDBuild schema administration and the optional demo backups. */

import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import { useToast } from '../../components/Toast';
import {
  cleanupDeprecatedSchema,
  provisionAssetRadarSchema,
  wipeAssetRadarData,
} from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { missingPermissionTitle } from '../../lib/permissions';
import { IMPORT_KLASS_LABELS } from '../../lib/types';
import type {
  SchemaCleanupResponse,
  SchemaProvisionResponse,
  SchemaWipeResponse,
} from '../../lib/types';
import OperationCard from '../admin/OperationCard';
import SchemaFileCard from '../admin/SchemaFileCard';
import DemoBackupsCard from './DemoBackupsCard';

/** Label of a known class in the UI language; unknown classes keep their raw name. */
function klassLabel(klass: string): string {
  const labels: Readonly<Record<string, string | undefined>> = IMPORT_KLASS_LABELS;
  return labels[klass] ?? klass;
}

export function SystemDataModelPage(): ReactNode {
  const { t } = useLingui();
  const { has, user } = useAuth();
  // Translated per render, so a language switch updates the permission reasons.
  const noSchemaAdmin = missingPermissionTitle('schema.admin');
  const noImportWrite = missingPermissionTitle('import.write');
  /** Typed exactly like this (in the UI language) before the wipe button arms. */
  const wipeConfirmWord = t({
    message: 'LÖSCHEN',
    comment: 'Confirmation word the user must type to wipe all data; keep it a single upper-case word.',
  });
  const toast = useToast();
  const canSchema = has('schema.admin');
  const canImport = has('import.write');

  const [cleanupConfirmed, setCleanupConfirmed] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState('');

  const report = useCallback(
    (message: string, errors: readonly string[]): void => {
      if (errors.length > 0) {
        const failures = errors.length;
        toast.error(t`${message} ${plural(failures, { one: '# Fehler', other: '# Fehler' })}, Details in der Karte.`);
      } else {
        toast.success(message);
      }
    },
    [t, toast],
  );

  const wipeArmed = wipeConfirm.trim().toUpperCase() === wipeConfirmWord;
  const demoBackupsDisabledReason = !canSchema
    ? noSchemaAdmin
    : !canImport
      ? noImportWrite
      : !user?.demo_reset_enabled
        ? t`Demo-Backups sind in dieser Installation deaktiviert.`
        : null;

  return (
    <>
      {user?.app_variant === 'demo' ? (
        <div className="card">
          <div className="card-head">
            <h2><Trans>Demo</Trans></h2>
          </div>
          <div className="card-body">
            <DemoBackupsCard disabledReason={demoBackupsDisabledReason} />
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card-head">
          <h2><Trans>CMDBuild-Schema</Trans></h2>
        </div>
        <div className="card-body">
          {!canSchema ? (
            <Banner kind="warn">
              <Trans>
                Schema-Operationen sind für Ihr Konto gesperrt: es fehlt die Berechtigung{' '}
                <code className="mono">schema.admin</code>.
              </Trans>
            </Banner>
          ) : null}
          <div className="ops-grid">
            <OperationCard<SchemaProvisionResponse>
              title={t`Schema anlegen / ergänzen`}
              description={t`Legt fehlende Klassen, Attribute, Domains, Lookups und Länder des AssetRadar-Datenmodells an und ergänzt den Ordner „AssetRadar“ im CMDBuild-Menü. Vorhandenes bleibt unverändert.`}
              buttonLabel={t`Schema provisionieren`}
              runningLabel={t`Provisioniere…`}
              disabledReason={canSchema ? null : noSchemaAdmin}
              run={provisionAssetRadarSchema}
              errorsOf={(response) => response.result.errors}
              onDone={(response, errors) => {
                const classes = response.result.created_classes.length;
                const attributes = response.result.created_attributes.length;
                report(t`Schema geprüft: ${classes} Klassen und ${attributes} Attribute neu.`, errors);
              }}
              summarise={(response) => {
                const result = response.result;
                const newClasses = result.created_classes.length;
                const newAttributes = result.created_attributes.length;
                const newDomains = result.created_domains.length;
                const newLookups = result.created_lookups.length;
                const newCountries = result.created_cards.length;
                const newMenuEntries = result.created_menu_entries.length;
                const oldClasses = result.skipped_classes.length;
                const oldAttributes = result.skipped_attributes.length;
                const oldDomains = result.skipped_domains.length;
                const oldLookups = result.skipped_lookups.length;
                const oldCountries = result.skipped_cards.length;
                return (
                  <Trans>
                    Neu: {newClasses} Klassen, {newAttributes} Attribute, {newDomains} Domains, {newLookups} Lookups,{' '}
                    {newCountries} Länder, {newMenuEntries} Menüeinträge. Bereits vorhanden: {oldClasses} Klassen,{' '}
                    {oldAttributes} Attribute, {oldDomains} Domains, {oldLookups} Lookups, {oldCountries} Länder.
                  </Trans>
                );
              }}
            />

            <SchemaFileCard disabledReason={canSchema ? null : noSchemaAdmin} onDone={report} />

            <OperationCard<SchemaCleanupResponse>
              title={t`Veraltete Schema-Objekte entfernen`}
              description={t`Löscht Klassen, Attribute und Domains aus früheren Modellständen samt der darin verbleibenden Karten. Nicht rückgängig machbar.`}
              buttonLabel={t`Aufräumen starten`}
              runningLabel={t`Räume auf…`}
              destructive
              disabledReason={
                !canSchema
                  ? noSchemaAdmin
                  : cleanupConfirmed
                    ? null
                    : t`Bitte zuerst bestätigen, dass veraltete Objekte samt Karten entfernt werden dürfen.`
              }
              run={cleanupDeprecatedSchema}
              errorsOf={(response) => response.result.errors}
              onDone={(response, errors) => {
                setCleanupConfirmed(false);
                const removed = response.result.removed.length;
                report(t`Aufräumen abgeschlossen: ${removed} Objekte entfernt.`, errors);
              }}
              summarise={(response) => {
                const removed = response.result.removed.length;
                return t`${plural(removed, {
                  one: '# Objekt entfernt. Details (Karten, Attribute, Domains, Klassen) im JSON.',
                  other: '# Objekte entfernt. Details (Karten, Attribute, Domains, Klassen) im JSON.',
                })}`;
              }}
            >
              <label className="check">
                <input
                  type="checkbox"
                  checked={cleanupConfirmed}
                  onChange={(event) => setCleanupConfirmed(event.target.checked)}
                  disabled={!canSchema}
                />
                <Trans>Ja, veraltete Objekte samt enthaltener Karten entfernen</Trans>
              </label>
            </OperationCard>

            <OperationCard<SchemaWipeResponse>
              title={t`Alle AssetRadar-Daten löschen`}
              description={t`Löscht sämtliche Karten der AssetRadar-Klassen in CMDBuild: Kunden, Produkte, Komponenten, CVEs, Events und Benachrichtigungen. Das Schema bleibt bestehen. Nicht rückgängig machbar.`}
              buttonLabel={t`Endgültig löschen (${wipeConfirmWord})`}
              runningLabel={t`Lösche…`}
              destructive
              disabledReason={
                !canSchema
                  ? noSchemaAdmin
                  : wipeArmed
                    ? null
                    : t`Bitte zur Bestätigung „${wipeConfirmWord}“ eingeben.`
              }
              run={wipeAssetRadarData}
              errorsOf={(response) => response.result.errors}
              onDone={(response, errors) => {
                setWipeConfirm('');
                const total = Object.values(response.result.deleted).reduce(
                  (sum, count) => sum + count,
                  0,
                );
                report(t`Datenbestand gelöscht: ${total} Karten entfernt.`, errors);
              }}
              summarise={(response) => {
                const entries = Object.entries(response.result.deleted).filter(
                  ([, count]) => count > 0,
                );
                const total = entries.reduce((sum, [, count]) => sum + count, 0);
                if (entries.length === 0) return t`${plural(total, { one: '# Karte gelöscht', other: '# Karten gelöscht' })}`;
                const breakdown = entries.map(([klass, count]) => `${klassLabel(klass)} ${count}`).join(', ');
                return t`${plural(total, { one: '# Karte gelöscht', other: '# Karten gelöscht' })}: ${breakdown}`;
              }}
            >
              <div className="field">
                <label htmlFor="ops-wipe-confirm">
                  <Trans>Zur Bestätigung „{wipeConfirmWord}“ eingeben</Trans>
                </label>
                <input
                  id="ops-wipe-confirm"
                  type="text"
                  value={wipeConfirm}
                  onChange={(event) => setWipeConfirm(event.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={!canSchema}
                  aria-describedby="ops-wipe-hint"
                />
                <span className="hint" id="ops-wipe-hint">
                  <Trans>Erst dann wird die Schaltfläche freigegeben.</Trans>
                </span>
              </div>
            </OperationCard>
          </div>
        </div>
      </div>
    </>
  );
}

export default SystemDataModelPage;
